"use client";

import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const SKILLS = ["HTML", "CSS", "Responsive", "Flexbox"];

export default function HtmlCssMetamorphosis() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const [cssOn, setCssOn] = useState(true);

    return (
        <section id="htmlcss-metamorphosis" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Html_meta_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Html_meta_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (HTML orange + CSS bleu) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="code-square-bold"
                            className="w-3.5 h-3.5"
                        />
                        portfolio.html
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Html_meta_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Bascule Sans CSS / Avec CSS */}
                    <div className="flex justify-center mb-7">
                        <div className="inline-flex items-center gap-1 rounded-full bg-black/30 ring-1 ring-white/10 p-1">
                            <button
                                type="button"
                                onClick={() => setCssOn(false)}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    !cssOn
                                        ? "bg-white/10 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Html_meta_toggle_off")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setCssOn(true)}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    cssOn
                                        ? "bg-orange-500 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Html_meta_toggle_on")}
                            </button>
                        </div>
                    </div>

                    {/* Aperçu : portfolio brut <-> stylé */}
                    <div className="flex items-center justify-center min-h-[400px]">
                        <AnimatePresence mode="wait">
                            {cssOn ? (
                                <motion.div
                                    key="styled"
                                    initial={
                                        reduce
                                            ? false
                                            : { opacity: 0, scale: 0.96 }
                                    }
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={
                                        reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98 }
                                    }
                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                    className="w-full max-w-[380px] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden"
                                >
                                    <div className="h-16 bg-gradient-to-r from-orange-400 to-sky-400" />
                                    <div className="px-6 pb-6 -mt-8">
                                        <span className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-sky-500 text-white text-xl font-bold ring-4 ring-white">
                                            MD
                                        </span>
                                        <h3 className="mt-3 text-lg font-bold text-slate-900">
                                            {t("Html_meta_name")}
                                        </h3>
                                        <p className="text-sm font-medium text-orange-600">
                                            {t("Html_meta_role")}
                                        </p>
                                        <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                                            {t("Html_meta_bio")}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {SKILLS.map((skill) => (
                                                <span
                                                    key={skill}
                                                    className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                                                >
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white transition-colors"
                                        >
                                            <SolarIcon
                                                name="letter-bold"
                                                className="w-4 h-4"
                                            />
                                            {t("Html_meta_contact")}
                                        </button>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="raw"
                                    initial={
                                        reduce ? false : { opacity: 0, scale: 0.98 }
                                    }
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={
                                        reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98 }
                                    }
                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                    className="w-full max-w-[380px] bg-white px-4 text-black"
                                    style={{ fontFamily: "Times New Roman, serif" }}
                                >
                                    {/* HTML brut : styles par défaut du navigateur */}
                                    <h1
                                        style={{
                                            fontSize: "1.8em",
                                            fontWeight: "bold",
                                            margin: "0.5em 0",
                                        }}
                                    >
                                        {t("Html_meta_name")}
                                    </h1>
                                    <p style={{ margin: "0.6em 0" }}>
                                        {t("Html_meta_role")}
                                    </p>
                                    <div
                                        style={{
                                            width: "120px",
                                            height: "88px",
                                            border: "1px solid #767676",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#767676",
                                            fontSize: "12px",
                                            margin: "0.6em 0",
                                        }}
                                    >
                                        {t("Html_meta_photo_alt")}
                                    </div>
                                    <p style={{ margin: "0.6em 0" }}>
                                        {t("Html_meta_bio")}
                                    </p>
                                    <p style={{ margin: "0.6em 0" }}>
                                        <b>{t("Html_meta_skills_label")} :</b>
                                    </p>
                                    <ul
                                        style={{
                                            listStyleType: "disc",
                                            paddingLeft: "2.5em",
                                            margin: "0.6em 0",
                                        }}
                                    >
                                        {SKILLS.map((skill) => (
                                            <li key={skill}>{skill}</li>
                                        ))}
                                    </ul>
                                    <a
                                        href="#"
                                        onClick={(e) => e.preventDefault()}
                                        style={{
                                            color: "#0000ee",
                                            textDecoration: "underline",
                                            display: "inline-block",
                                            margin: "0.6em 0",
                                        }}
                                    >
                                        {t("Html_meta_contact")}
                                    </a>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Légende dynamique */}
                    <p className="mt-6 text-center text-sm text-gray-400 max-w-[560px] mx-auto mb-0">
                        {cssOn
                            ? t("Html_meta_hint_on")
                            : t("Html_meta_hint_off")}
                    </p>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Html_meta_caption")}
            </p>
        </section>
    );
}
